import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Flame } from 'lucide-react';
import BlurFade from '../primitives/BlurFade';
import CountUp from '../primitives/CountUp';
import { Section } from '../layout/Section';
import { Container } from '../layout/Container';
import { Eyebrow, Headline } from '../layout/typography';
import { useLang } from '../../LangContext';
import { getProduct } from '../../catalog';
import BundleDisclaimer from '../BundleDisclaimer';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '../ui/hover-card';

/**
 * BundlesBlock — cross-sell packages on the homepage.
 *
 * Each card shows:
 *   • Bundle name + optional "Populär" pin + optional stockLeft sticker
 *   • Composition (e.g. "5× Spannrahmen · 2× Plissee · 1× Pendeltür")
 *   • Big bundle price, strike-through original, "Spare €X (Y%)" badge
 *   • CTA → /offerte with a pre-filled bundle reference
 *
 * Subtle psychology touch: when liveCounter is enabled in prices.json,
 * the stock badge starts at stockLeft + delta (delta=1–maxDelta picked
 * randomly per card per page-load) and ticks down to the real stockLeft
 * shortly after the section scrolls into view. Visually: short strike-
 * through "X+delta → X" transition. Once-per-mount, no infinite loop.
 *
 * Renders nothing when `data` is null (bundles disabled in prices.json).
 */

// Session-only memory: once a bundle ticked down on this page load, don't
// re-tick if the user scrolls back up and down again.
const _ticked = new Set();

function BundleCard({ data, b, lang }) {
  const composition = b.products.map((p) => {
    const product = getProduct(lang, p.category, p.slug);
    return product ? `${p.qty}× ${product.name}` : null;
  }).filter(Boolean).join('  ·  ');
  // composition is built for the visible <li> list below; the unused
  // string variant is kept around for future use (e.g. tooltip/aria).
  void composition;

  const ctaHref = `/configurator?bundle=${encodeURIComponent(b.id)}`;
  const stockLow = b.stockLeft != null && b.stockLeft > 0 && b.stockLeft <= 5;

  // Live-counter logic. Picks a one-time random delta per card and
  // ticks down to the real stockLeft when the card enters the viewport.
  const liveOn = !!data.liveCounter?.enabled && b.stockLeft != null && b.stockLeft > 0 && !b.soldOut;
  const initialDelta = useMemo(() => {
    if (!liveOn) return 0;
    if (_ticked.has(b.id)) return 0; // already settled this session
    const max = Math.max(1, data.liveCounter?.maxDelta || 2);
    return 1 + Math.floor(Math.random() * max); // 1..max
  }, [liveOn, b.id, data.liveCounter?.maxDelta]);

  const [displayedStock, setDisplayedStock] = useState(b.stockLeft + initialDelta);
  const [phase, setPhase] = useState(initialDelta > 0 ? 'pre' : 'settled');
  const articleRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    // Only run the live-counter sequence once per mount.
    if (startedRef.current || initialDelta === 0 || !articleRef.current) return;
    const node = articleRef.current;
    const timers = [];
    const obs = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      obs.disconnect();
      startedRef.current = true;
      const stagger = 400 + Math.random() * 600;
      timers.push(setTimeout(() => {
        setPhase('ticking');
        timers.push(setTimeout(() => {
          setDisplayedStock(b.stockLeft);
          timers.push(setTimeout(() => {
            setPhase('settled');
            _ticked.add(b.id);
          }, 1400));
        }, 220));
      }, stagger));
    }, { threshold: 0.35 });
    obs.observe(node);
    return () => {
      obs.disconnect();
      timers.forEach(clearTimeout);
    };
    // Intentionally only mount-once: tying to b.id/stockLeft would re-fire
    // the sequence on data refresh which we don't want.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build the badge label using the displayedStock value (not the raw
  // one from props) — we need to swap the number inside b.stockBadge.
  // b.stockBadge already contains stockLeft baked in; rebuild for live mode.
  const badgeLabel = b.stockBadge
    ? (liveOn ? b.stockBadge.replace(String(b.stockLeft), String(displayedStock)) : b.stockBadge)
    : null;

  return (
    <article
      ref={articleRef}
      data-testid={`bundle-card-${b.id}`}
      className={`group relative h-full flex flex-col rounded-[22px] p-7 lg:p-8 transition-all duration-500 ${
        b.soldOut ? 'opacity-60' : 'hover:-translate-y-1'
      } ${
        b.highlight
          ? 'bg-[#1F1D1A] text-white border border-[#F26B1A]/60 shadow-[0_30px_60px_-30px_rgba(242,107,26,0.45)]'
          : 'bg-white border border-[#E5E0D2] hover:border-[#F26B1A]/40 hover:shadow-[0_30px_60px_-30px_rgba(31,29,26,0.18)]'
      }`}
    >
      {/* Top-right sticker stack: Popular pin + custom Badge.
          Stock badge moved inline next to the save pill so the top stack
          never overlaps the headline/tagline area. */}
      <div className="absolute -top-3 right-6 flex flex-col items-end gap-2">
        {b.highlight && !b.soldOut && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F26B1A] text-white text-[10.5px] uppercase tracking-[0.18em] font-semibold shadow-[0_4px_14px_-4px_rgba(242,107,26,0.55)]">
            <Sparkles className="w-3 h-3" /> {data.popularLabel}
          </span>
        )}
        {b.tagBadge && !b.soldOut && (
          <span
            data-testid={`bundle-${b.id}-tag`}
            className="inline-flex items-center px-3 py-1 rounded-full text-white text-[10.5px] uppercase tracking-[0.18em] font-semibold shadow-[0_4px_14px_-4px_rgba(31,29,26,0.25)]"
            style={{ backgroundColor: b.tagBadge.color }}
          >
            {b.tagBadge.label}
          </span>
        )}
      </div>

      <h3 className={`font-display text-[26px] lg:text-[30px] leading-tight ${b.highlight ? 'text-white' : 'text-[#1F1D1A]'}`}>
        {b.name}
      </h3>
      {b.tagline && (
        <p className={`mt-1.5 text-[14px] leading-snug ${b.highlight ? 'text-white/70' : 'text-[#5C5852]'}`}>
          {b.tagline}
        </p>
      )}

      {/* Composition + transparent unit-price breakdown.
          Each line shows: qty × productName ……… qty×unit = lineTotal
          so users can verify the bundle math against the catalog prices. */}
      <ul className={`mt-6 pt-6 border-t space-y-2 ${b.highlight ? 'border-white/15' : 'border-[#E5E0D2]'}`}>
        {b.products.map((p, j) => {
          const product = getProduct(lang, p.category, p.slug);
          if (!product) return null;
          const bd = b.breakdown ? b.breakdown[j] : null;
          return (
            <li key={`${p.category}-${p.slug}`} className="flex items-baseline justify-between gap-3 text-[14.5px]">
              <span className={b.highlight ? 'text-white/85' : 'text-[#1F1D1A]/85'}>
                <span className="font-semibold text-[#F26B1A]">{p.qty}×</span>{' '}
                {product.name}
              </span>
              {bd && (
                <span className={`tabular-nums text-[12.5px] whitespace-nowrap ${b.highlight ? 'text-white/60' : 'text-[#5C5852]'}`}>
                  {bd.displayUnit}
                </span>
              )}
            </li>
          );
        })}
        {/* Subtotal — sum of line totals */}
        {b.breakdown && b.breakdown.length > 1 && (
          <li className={`flex items-baseline justify-between gap-3 text-[12.5px] pt-2 mt-1 border-t ${b.highlight ? 'border-white/15 text-white/75' : 'border-[#E5E0D2] text-[#5C5852]'}`}>
            <span className="uppercase tracking-[0.14em] font-semibold">{data.subtotalLabel}</span>
            <span className="tabular-nums font-semibold">{b.displayBase}</span>
          </li>
        )}
      </ul>

      {/* Price block */}
      <div className="mt-7 flex flex-col gap-2">
        <div className="flex items-baseline gap-3 flex-wrap">
          <p className={`font-display text-[40px] lg:text-[48px] leading-none ${b.highlight ? 'text-white' : 'text-[#1F1D1A]'}`}>
            {b.displayPrice}
            <span
              aria-hidden="true"
              className={`ml-1 align-super text-[14px] font-normal ${b.highlight ? 'text-white/55' : 'text-[#5C5852]'}`}
            >
              *
            </span>
          </p>
          {b.save > 0 && (
            <p className={`text-[14px] ${b.highlight ? 'text-white/55' : 'text-[#5C5852]'}`}>
              <span className="opacity-70">{data.insteadLabel}</span>{' '}
              <span className="line-through">{b.displayBase}</span>
            </p>
          )}
        </div>
        {(b.save > 0 || b.stockBadge) && (
          <div className="flex flex-wrap items-center gap-2">
            {b.save > 0 && (
              <span
                data-testid={`bundle-${b.id}-save`}
                className="inline-flex items-baseline gap-1.5 px-2.5 py-1 rounded-full bg-[#F26B1A] text-white text-[11px] uppercase tracking-[0.15em] font-semibold tabular-nums"
              >
                <span>{data.saveLabel}</span>
                <CountUp to={b.save} duration={1.4} suffix=",-- €" />
                <span aria-hidden="true" className="opacity-70">·</span>
                <span>−<CountUp to={b.savePct} duration={1.4} suffix="%" /></span>
              </span>
            )}
            {b.stockBadge && (
              <span
                data-testid={`bundle-${b.id}-stock`}
                data-stock-phase={phase}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] uppercase tracking-[0.15em] font-semibold transition-all duration-300 ${
                  b.soldOut
                    ? 'bg-[#5C5852] text-white'
                    : stockLow && phase === 'settled'
                      ? 'bg-[#F26B1A] text-white animate-pulse'
                      : (b.highlight
                          ? 'bg-white/10 text-white border border-white/25'
                          : 'bg-white text-[#1F1D1A] border border-[#F26B1A]/40')
                } ${phase === 'ticking' ? 'scale-105 line-through decoration-2 decoration-[#F26B1A]' : ''}`}
              >
                {!b.soldOut && <Flame className="w-3 h-3" />}
                <span className="tabular-nums">{badgeLabel}</span>
              </span>
            )}
          </div>
        )}
      </div>

      {/* CTA */}
      {b.soldOut ? (
        <span
          data-testid={`bundle-${b.id}-cta-disabled`}
          className="mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[13.5px] font-medium bg-[#5C5852]/20 text-[#5C5852] cursor-not-allowed"
        >
          {data.soldOutLabel || '—'}
        </span>
      ) : (
        <HoverCard openDelay={120} closeDelay={80}>
          <HoverCardTrigger asChild>
            <Link
              to={ctaHref}
              data-testid={`bundle-${b.id}-cta`}
              data-lead-source={`bundle-${b.id}`}
              className={`mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[13.5px] font-medium transition-colors ${
                b.highlight
                  ? 'bg-[#F26B1A] text-white hover:bg-[#D85A0A]'
                  : 'bg-[#1F1D1A] text-white hover:bg-[#F26B1A]'
              }`}
            >
              {data.ctaLabel} <ArrowRight className="w-4 h-4" />
            </Link>
          </HoverCardTrigger>
          <HoverCardContent
            side="top"
            align="center"
            sideOffset={10}
            className="w-[300px] p-0 border-none shadow-[0_20px_50px_-15px_rgba(31,29,26,0.35)]"
          >
            <BundleDisclaimer
              data-testid={`bundle-${b.id}-hint`}
              labels={data}
              variant="card"
              title={lang === 'de' ? 'Hinweis zum Paketpreis' : 'Toelichting pakketprijs'}
            />
          </HoverCardContent>
        </HoverCard>
      )}
    </article>
  );
}

export default function BundlesBlock({ data }) {
  const { lang } = useLang();
  if (!data) return null;

  return (
    <Section id="bundles" data-testid="bundles-section" tone="cream" rhythm="relaxed">
      <Container>
        <div className="mb-14 lg:mb-20">
          <BlurFade><Eyebrow className="mb-7">{data.eyebrow}</Eyebrow></BlurFade>
          <BlurFade delay={0.05}>
            <Headline size="lg" className="md:whitespace-nowrap">{data.title}</Headline>
          </BlurFade>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {data.items.map((b, i) => (
            <BlurFade key={b.id} delay={0.06 * i} offset={18}>
              <BundleCard data={data} b={b} lang={lang} />
            </BlurFade>
          ))}
        </div>

        {(data.whiteStandardNote || data.vatNote || data.shippingNote) && (
          <BlurFade delay={0.1}>
            <div
              data-testid="bundles-disclaimer"
              className="mt-10 lg:mt-14 max-w-3xl text-[12.5px] leading-relaxed text-[#5C5852] space-y-1.5"
            >
              {data.whiteStandardNote && (
                <p data-testid="bundles-disclaimer-config">
                  <span className="font-semibold text-[#1F1D1A]">*</span> {data.whiteStandardNote}
                </p>
              )}
              {(data.vatNote || data.shippingNote) && (
                <p data-testid="bundles-disclaimer-vat-ship" className="text-[#7A746B]">
                  {data.vatNote}{data.vatNote && data.shippingNote ? ' · ' : ''}{data.shippingNote}
                </p>
              )}
            </div>
          </BlurFade>
        )}
      </Container>
    </Section>
  );
}
